<script setup lang="ts">
import { supabaseIsOnline, useSupabaseAnon } from '~/composables/supabase'
// defineOgImageComponent('NuxtSeo')

type EventCard = {
  id: string
  name: string
  status: string
  location: string
  displayDate: string
}

const events = ref<EventCard[]>([])
const isLoading = ref(false)
const loadFailed = ref(false)
let loadVersion = 0

const loadEvents = async () => {
  const version = ++loadVersion
  isLoading.value = true
  loadFailed.value = false
  try {
    const { data, error } = await useSupabaseAnon()
      .from('event')
      .select(
        'id, name, status, location, display_date, start_date, end_date, time_zone'
      )

    if (version !== loadVersion) return
    if (error) throw error

    const now = new Date()
    const orderedEvents = (data ?? [])
      .map((event) => {
        const parts = new Intl.DateTimeFormat('en', {
          timeZone: event.time_zone,
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
        }).formatToParts(now)
        const { year, month, day } = Object.fromEntries(
          parts.map((part) => [part.type, part.value])
        )
        const today = `${year}-${month}-${day}`
        // Ongoing, upcoming, then past, using each event's local calendar date.
        const phase =
          event.end_date < today ? 2 : event.start_date > today ? 1 : 0
        return { event, phase }
      })
      .sort(
        (a, b) =>
          a.phase - b.phase ||
          (a.phase === 2
            ? b.event.start_date.localeCompare(a.event.start_date)
            : a.event.start_date.localeCompare(b.event.start_date)) ||
          a.event.name.localeCompare(b.event.name) ||
          a.event.id.localeCompare(b.event.id)
      )
    events.value = orderedEvents.map(({ event }) => ({
      id: event.id,
      name: event.name,
      status: event.status,
      location: event.location,
      displayDate: event.display_date,
    }))
  } catch (error) {
    if (version !== loadVersion) return
    console.error(error)
    loadFailed.value = true
  } finally {
    if (version === loadVersion) isLoading.value = false
  }
}

watch(supabaseIsOnline, (online) => {
  if (online && (loadFailed.value || isLoading.value)) void loadEvents()
})

onUnmounted(() => {
  loadVersion++
})

// Check if #test is in the URL hash
const isTestMode = ref(false)

onMounted(() => {
  // Function to check hash
  const checkHash = () => {
    isTestMode.value = window.location.hash === '#test'
  }

  // Check initial hash
  checkHash()
  loadEvents()
})
</script>

<template>
  <div class="m-1">
    <h1 class="text-3xl text-primary banner">
      Welcome to AgilityNerd Compete!
    </h1>
    <p class="no-print">
      Compete is an easy to use and customize walk/run schedule for your dogs.
      Each event's schedule is based on the published schedule documents.
    </p>

    <p class="no-print">Features:</p>
    <ul class="list-disc list-outside no-print mb-8">
      <li>
        <NuxtLink to="/help">Add to your phone's home screen</NuxtLink>
        for fast access to your schedule.
      </li>
      <li>Add your friends to your schedule and don't miss their runs!</li>
      <li>
        Watching the
        <a
          target="_blank"
          href="https://4leggedflix.com/"
        >
          4 Legged Flix
        </a>
        live stream? See your friend's ring numbers and run times in your
        timezone!
      </li>
    </ul>

    <ClientOnly>
      <div
        v-if="loadFailed"
        class="no-print flex flex-col items-center gap-3 my-4"
      >
        <p
          role="alert"
          class="text-error text-center m-0"
        >
          We couldn't load the events. Please try again.
        </p>
        <button
          type="button"
          class="btn btn-primary"
          @click="loadEvents"
        >
          Retry
        </button>
      </div>
      <p
        v-else-if="isLoading"
        class="no-print text-center"
      >
        Loading events…
      </p>
      <div
        v-if="events.length"
        class="grid grid-cols-1 sm:grid-cols-2 gap-4"
      >
        <div
          v-for="(event, index) in events"
          :key="event?.id?.toString() || index"
          class="card bg-base-100 shadow-xl card-compact w-full border-secondary border-solid border"
        >
          <div class="card-body">
            <h2 class="card-title mt-1">
              <NuxtLink
                v-if="
                  ['archived', 'active', 'testing'].includes(event.status) ||
                  (event.status === 'coming' && isTestMode)
                "
                :to="`event-${event.name}${isTestMode ? '#test' : ''}`"
              >
                {{ event.name }}
              </NuxtLink>
              <span v-else>{{ event.name }}</span>
            </h2>
            <div
              v-if="event.status == 'coming'"
              class="text-success text-lg font-bold"
            >
              Coming Soon!
            </div>
            <div
              v-else-if="event.status == 'archived'"
              class="text-error text-lg font-bold"
            >
              Archived
            </div>
            <div
              v-else-if="event.status == 'testing'"
              class="text-error text-lg font-bold"
            >
              Testing
            </div>
            <div
              v-else
              class="text-success text-lg font-bold"
            >
              Active!
            </div>
            <div class="text-base">{{ event.location }}</div>
            <div class="badge badge-primary">{{ event.displayDate }}</div>
          </div>
        </div>
      </div>
    </ClientOnly>
  </div>
</template>

<style scoped>
.banner {
  font-family:
    Kaushan Script,
    ui-sans-serif,
    system-ui,
    sans-serif;
}
</style>
